import { useCallback, useEffect, useMemo, useState } from 'react'
import { Layers } from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api'
import {
  addCriterionToList,
  computeContradictoryKeys,
  computeIsStale,
  removeCriterionFromList,
  sortedTagIdsKey,
} from '../lib/resourceGroupCriteria'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import { ConfirmDialog } from '../components/ConfirmDialog'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

const PAGE_SIZE = 25
const MAX_CRITERIA = 10

interface TagOption {
  id: string
  key: string
  value: string
  tag_key_id: string
}

interface Criterion {
  tag_id: string
  key: string
  value: string
}

interface TagGroup {
  id: string
  name: string
  description?: string
  member_count?: number
  criteria?: Criterion[]
}

interface Member {
  resource_id: string
  computed_at: string
}

interface PagedResponse<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

interface TagKeyOption {
  id: string
  key: string
  enabled: boolean
  multi_value: boolean
}

// ---------------------------------------------------------------------------
// Criteria chips — used in both the list table and the members drawer.
// AND-separated, collapses beyond maxVisible with a "+N more" expander.
// ---------------------------------------------------------------------------
function CriteriaChips({ criteria, maxVisible = 2 }: { criteria: Criterion[]; maxVisible?: number }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? criteria : criteria.slice(0, maxVisible)
  const hidden = criteria.length - visible.length

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((c, i) => (
        <span key={c.tag_id} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-[10px] font-semibold text-muted">AND</span>}
          <Badge variant="default">{c.key}: {c.value}</Badge>
        </span>
      ))}
      {hidden > 0 && (
        <>
          <span className="text-[10px] font-semibold text-muted">AND</span>
          <button type="button" className="text-xs text-primary hover:underline" onClick={() => setExpanded(true)}>
            +{hidden} more
          </button>
        </>
      )}
      {expanded && criteria.length > maxVisible && (
        <button type="button" className="text-xs text-muted hover:underline" onClick={() => setExpanded(false)}>
          show less
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Value combobox — search + select popover for a criterion's value, scoped
// to whichever key was picked.
// ---------------------------------------------------------------------------
function ValueCombobox({
  options,
  disabled,
  onQueryChange,
  onSelect,
}: {
  options: TagOption[]
  disabled?: boolean
  onQueryChange: (q: string) => void
  onSelect: (tag: TagOption) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  return (
    <div className="relative w-40">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm text-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        Select value
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-56 overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
          <div className="border-b border-border p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); onQueryChange(e.target.value) }}
              placeholder="Search values…"
              className="w-full px-1 py-0.5 text-sm outline-none"
            />
          </div>
          <div className="max-h-40 overflow-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted">No matches</div>
            ) : (
              options.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => { onSelect(o); setOpen(false); setQuery(''); onQueryChange('') }}
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-surface-muted"
                >
                  {o.value}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminTagGroupsPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [groups, setGroups] = useState<TagGroup[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [tagKeys, setTagKeys] = useState<TagKeyOption[]>([])
  const [criteriaOptions, setCriteriaOptions] = useState<TagOption[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [criteria, setCriteria] = useState<Criterion[]>([])
  const [addCriterionKeyId, setAddCriterionKeyId] = useState('')
  const [attemptedSave, setAttemptedSave] = useState(false)
  const [saving, setSaving] = useState(false)

  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [lastPreviewedIds, setLastPreviewedIds] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [membersFor, setMembersFor] = useState<TagGroup | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [membersLoading, setMembersLoading] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => { setPage(1) }, [debouncedSearch])

  const loadTagKeys = useCallback(() => {
    if (!tenantId) return
    apiGet<TagKeyOption[]>(`/api/v1/tenants/${tenantId}/tag-keys`)
      .then((d) => setTagKeys((d ?? []).filter((k) => k.enabled)))
      .catch(() => setTagKeys([]))
  }, [tenantId])

  useEffect(() => { loadTagKeys() }, [loadTagKeys])

  const loadGroups = useCallback(() => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    const offset = (page - 1) * PAGE_SIZE
    const q = debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : ''
    apiGet<PagedResponse<TagGroup>>(`/api/v1/tenants/${tenantId}/tag-groups?limit=${PAGE_SIZE}&offset=${offset}${q}`)
      .then((d) => { setGroups(d.items ?? []); setTotal(d.total ?? 0) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [tenantId, page, debouncedSearch])

  useEffect(() => { loadGroups() }, [loadGroups])

  const criteriaTagIds = useMemo(() => criteria.map((c) => c.tag_id), [criteria])

  const contradictoryKeys = useMemo(
    () => computeContradictoryKeys(criteria, tagKeys),
    [criteria, tagKeys],
  )

  const openCreate = () => {
    setEditId(null)
    setFormName('')
    setFormDescription('')
    setCriteria([])
    setPreviewCount(null)
    setLastPreviewedIds(null)
    setAttemptedSave(false)
    setAddCriterionKeyId('')
    setShowForm(true)
  }

  const openEdit = (g: TagGroup) => {
    setEditId(g.id)
    setFormName(g.name)
    setFormDescription(g.description ?? '')
    setCriteria(g.criteria ?? [])
    setPreviewCount(g.member_count ?? null)
    setLastPreviewedIds(sortedTagIdsKey((g.criteria ?? []).map((c) => c.tag_id)))
    setAttemptedSave(false)
    setAddCriterionKeyId('')
    setShowForm(true)
  }

  const fetchCriteriaOptions = useCallback((keyId: string, q: string) => {
    if (!tenantId || !keyId) { setCriteriaOptions([]); return }
    const qs = new URLSearchParams({ limit: '50', tag_key_id: keyId })
    if (q.trim()) qs.set('q', q.trim())
    apiGet<PagedResponse<TagOption>>(`/api/v1/tenants/${tenantId}/tags?${qs}`)
      .then((d) => setCriteriaOptions(d.items ?? []))
      .catch(() => setCriteriaOptions([]))
  }, [tenantId])

  useEffect(() => {
    if (addCriterionKeyId) fetchCriteriaOptions(addCriterionKeyId, '')
  }, [addCriterionKeyId, fetchCriteriaOptions])

  const addCriterion = (tag: TagOption) => {
    const { criteria: next, error } = addCriterionToList(criteria, tag, MAX_CRITERIA)
    if (error) {
      toast({ title: error, variant: 'danger' })
      return
    }
    if (next === criteria) return // no-op: tag already selected, nothing actually changed
    setCriteria(next)
    setPreviewCount(null)
  }

  const removeCriterion = (tagId: string) => {
    setCriteria((prev) => removeCriterionFromList(prev, tagId))
    setPreviewCount(null)
  }

  const isStale = computeIsStale(lastPreviewedIds, criteriaTagIds)

  const runPreview = async () => {
    if (criteriaTagIds.length === 0) { setPreviewCount(0); return }
    setPreviewLoading(true)
    try {
      const p = await apiPost<{ match_count: number }>(`/api/v1/tenants/${tenantId}/tag-groups/preview`, {
        tag_ids: criteriaTagIds,
      })
      setPreviewCount(p.match_count)
      setLastPreviewedIds(sortedTagIdsKey(criteriaTagIds))
    } catch {
      setPreviewCount(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSave(true)
    if (!formName.trim() || criteria.length === 0) return
    setSaving(true)
    try {
      const payload = { name: formName.trim(), description: formDescription.trim(), tag_ids: criteriaTagIds }
      if (editId) {
        await apiPatch(`/api/v1/tenants/${tenantId}/tag-groups/${editId}`, payload)
        toast({ title: 'Resource group updated', variant: 'success' })
      } else {
        await apiPost(`/api/v1/tenants/${tenantId}/tag-groups`, payload)
        toast({ title: 'Resource group created', variant: 'success' })
      }
      setShowForm(false)
      loadGroups()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      toast({ title: 'Save failed', description: msg, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/tag-groups/${deleteId}`)
      setDeleteId(null)
      loadGroups()
      toast({ title: 'Resource group deleted', variant: 'success' })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed'
      toast({ title: 'Delete failed', description: msg, variant: 'danger' })
    } finally {
      setDeleting(false)
    }
  }

  const openMembers = (g: TagGroup) => {
    setMembersFor(g)
    setMembersLoading(true)
    apiGet<PagedResponse<Member>>(`/api/v1/tenants/${tenantId}/tag-groups/${g.id}/members?limit=200`)
      .then((d) => setMembers(d.items ?? []))
      .catch(() => setMembers([]))
      .finally(() => setMembersLoading(false))
  }

  if (showForm) {
    return (
      <div>
        <PageHeader
          title={editId ? 'Edit resource group' : 'New resource group'}
          actions={<Button variant="outline" onClick={() => setShowForm(false)}>Back to list</Button>}
        />
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <FormField label="Name" htmlFor="tg-name" error={attemptedSave && !formName.trim() ? 'Name is required.' : undefined}>
            <Input id="tg-name" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. HR" />
          </FormField>
          <FormField label="Description" htmlFor="tg-desc">
            <Input id="tg-desc" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Optional" />
          </FormField>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Criteria (AND)</p>
            {criteria.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {criteria.map((c) => (
                  <Badge key={c.tag_id} variant="primary" className="gap-1">
                    {c.key}: {c.value}
                    <button type="button" className="ml-1" onClick={() => removeCriterion(c.tag_id)}>×</button>
                  </Badge>
                ))}
              </div>
            )}

            {criteria.length < MAX_CRITERIA && (
              <div className="flex flex-wrap items-start gap-2">
                <Select className="w-40" value={addCriterionKeyId} onChange={(e) => setAddCriterionKeyId(e.target.value)}>
                  <option value="">Key</option>
                  {tagKeys.map((k) => (
                    <option key={k.id} value={k.id}>{k.key}</option>
                  ))}
                </Select>
                <ValueCombobox
                  options={criteriaOptions}
                  disabled={!addCriterionKeyId}
                  onQueryChange={(q) => addCriterionKeyId && fetchCriteriaOptions(addCriterionKeyId, q)}
                  onSelect={addCriterion}
                />
              </div>
            )}

            {contradictoryKeys.length > 0 && (
              <div className="mt-2 rounded-lg border border-warning/30 bg-warning-muted px-3 py-2 text-xs text-warning-foreground">
                Two conditions on <strong>{contradictoryKeys.join(', ')}</strong> combined with AND usually match nothing,
                since a resource can only carry one value for {contradictoryKeys.length === 1 ? 'that key' : 'those keys'}.
              </div>
            )}

            <div className="mt-3 flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={runPreview} disabled={previewLoading}>
                {previewLoading ? 'Checking…' : 'Preview match count'}
              </Button>
              {previewCount !== null && !previewLoading && (
                <span className="text-sm text-muted">
                  Matches <span className="font-semibold text-foreground">{previewCount}</span> {previewCount === 1 ? 'resource' : 'resources'}
                  {isStale && <span className="ml-2 text-warning-foreground">(criteria changed, preview again)</span>}
                </span>
              )}
            </div>

            {attemptedSave && criteria.length === 0 && (
              <div className="mt-2 rounded-lg border border-danger/30 bg-danger-muted px-3 py-2 text-xs text-danger-foreground">
                Add at least one criterion before saving.
              </div>
            )}
            {previewCount === 0 && !isStale && criteria.length > 0 && (
              <div className="mt-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-xs text-muted">
                This currently matches 0 resources. You can still save it, but no policy bound to it will apply yet.
              </div>
            )}
          </div>

          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </form>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="Resource groups" actions={<Button onClick={openCreate}>New resource group</Button>} />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <div className="mb-4">
        <Input
          placeholder="Search resource groups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Layers className="h-8 w-8" />}
          title={debouncedSearch ? 'No groups match' : 'No resource groups yet'}
          description="Define a group as an AND of tags, then attach policies to it."
          actionLabel="New resource group"
          onAction={openCreate}
        />
      ) : (
        <>
          <DataTable>
            <THead>
              <Tr>
                <Th>Name</Th>
                <Th>Criteria</Th>
                <Th>Members</Th>
                <Th className="text-right">Actions</Th>
              </Tr>
            </THead>
            <tbody>
              {groups.map((g) => (
                <Tr key={g.id}>
                  <Td className="font-medium">{g.name}</Td>
                  <Td><CriteriaChips criteria={g.criteria ?? []} /></Td>
                  <Td>
                    <button type="button" className="text-sm text-primary hover:underline" onClick={() => openMembers(g)}>
                      {g.member_count ?? 0}
                    </button>
                  </Td>
                  <Td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)}>Edit</Button>
                    <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleteId(g.id)}>Delete</Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <Modal
        open={!!membersFor}
        onClose={() => setMembersFor(null)}
        title={membersFor?.name ?? ''}
        footer={<Button variant="outline" onClick={() => setMembersFor(null)}>Close</Button>}
      >
        {membersFor && <CriteriaChips criteria={membersFor.criteria ?? []} />}
        <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-muted">
          {membersFor?.member_count ?? 0} matching resources
        </p>
        {membersLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : members.length === 0 ? (
          <p className="text-sm text-muted">No resources currently match.</p>
        ) : (
          <div className="max-h-72 space-y-1.5 overflow-auto">
            {members.map((m) => (
              <div key={m.resource_id} className="rounded-lg border border-border px-3 py-2 text-sm">
                {m.resource_id}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete resource group"
        message="Policies targeting this group will no longer expand to its members."
        confirmLabel="Delete"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteId(null)}
      />
    </div>
  )
}
